import Vue from 'vue'
import Vuex from 'vuex'
import { getUserInfo } from '@/utils/auth'

Vue.use(Vuex)

// 从localStorage恢复权限数据
function getPermissionCodesFromStorage() {
  try {
    const codes = localStorage.getItem('permissionCodes')
    return codes ? JSON.parse(codes) : []
  } catch (error) {
    console.error('解析权限码失败:', error)
    return []
  }
}

function getPermissionTreeFromStorage() {
  try {
    const tree = localStorage.getItem('permissionTree')
    return tree ? JSON.parse(tree) : []
  } catch (error) {
    console.error('解析权限树失败:', error)
    return []
  }
}

export default new Vuex.Store({
  state: {
    // 用户信息 - 从localStorage恢复
    userInfo: getUserInfo(),
    // 用户令牌
    token: localStorage.getItem('token') || null,
    // 权限码列表（扁平化，方便快速检查）
    permissionCodes: getPermissionCodesFromStorage(),
    // 权限树（包含层级结构）
    permissionTree: getPermissionTreeFromStorage(),
    // 菜单权限列表
    menuPermissions: [],
    // 按钮权限列表
    buttonPermissions: [],
    // API权限列表
    apiPermissions: []
  },
  getters: {
    // 获取用户信息
    userInfo: state => state.userInfo,
    // 获取令牌
    token: state => state.token,
    // 判断用户是否已登录
    isLoggedIn: state => !!state.token,
    // 获取权限码列表
    permissionCodes: state => state.permissionCodes,
    // 获取权限树
    permissionTree: state => state.permissionTree,
    // 检查是否有指定权限
    hasPermission: (state) => (permissionCode) => {
      if (!permissionCode) return false
      return state.permissionCodes && state.permissionCodes.includes(permissionCode)
    },
    // 检查是否有任意一个权限
    hasAnyPermission: (state) => (permissionCodes) => {
      if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
        return false
      }
      const userPermissions = state.permissionCodes || []
      return permissionCodes.some(code => userPermissions.includes(code))
    },
    // 检查是否有所有权限
    hasAllPermissions: (state) => (permissionCodes) => {
      if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
        return false
      }
      const userPermissions = state.permissionCodes || []
      return permissionCodes.every(code => userPermissions.includes(code))
    },
    // 从权限树构建菜单树（用于菜单组件）
    menuTreeFromPermissions: (state) => {
      // 如果权限树为空，返回空数组（后续会使用旧版menus作为后备）
      if (!state.permissionTree || state.permissionTree.length === 0) {
        return []
      }
      
      // 转换权限树为菜单树
      function convertPermissionToMenu(permission) {
        // 递归处理子菜单，只包含showInMenu为true的菜单
        const children = permission.children && permission.children.length > 0
          ? permission.children
              .filter(child => {
                // 子菜单必须是MENU类型，且showInMenu为true
                return child.permissionType === 'MENU' && (child.showInMenu === true || child.showInMenu === undefined)
              })
              .map(convertPermissionToMenu)
          : []
        
        return {
          id: permission.id,
          name: permission.permissionName,
          path: permission.resourcePath,
          icon: permission.description, // 图标信息在description中
          parentId: permission.parentId,
          sort: permission.sortOrder,
          permissionCode: permission.permissionCode,
          showInMenu: permission.showInMenu,
          children: children.length > 0 ? children : undefined
        }
      }
      
      // 只包含showInMenu为true的顶层菜单
      // 如果showInMenu字段不存在，默认为true（向后兼容）
      return state.permissionTree
        .filter(perm => {
          return perm.permissionType === 'MENU' && 
                 (perm.showInMenu === true || perm.showInMenu === undefined)
        })
        .map(convertPermissionToMenu)
        .filter(menu => {
          // 如果菜单没有子菜单，但showInMenu为false，则不显示
          // 如果有子菜单，即使自身showInMenu为false，如果有子菜单要显示，也应该显示父菜单
          if (menu.children && menu.children.length > 0) {
            return true // 有子菜单就显示父菜单
          }
          return menu.showInMenu === true || menu.showInMenu === undefined
        })
    }
  },
  mutations: {
    // 设置用户信息
    SET_USER_INFO(state, userInfo) {
      state.userInfo = userInfo
    },
    // 设置令牌
    SET_TOKEN(state, token) {
      state.token = token
      // 同步存储到本地
      if (token) {
        localStorage.setItem('token', token)
      } else {
        localStorage.removeItem('token')
      }
    },
    // 清除用户信息
    CLEAR_USER_INFO(state) {
      state.userInfo = null
    },
    // 设置权限码列表
    SET_PERMISSION_CODES(state, codes) {
      state.permissionCodes = codes || []
      // 同步存储到localStorage
      if (codes && codes.length > 0) {
        localStorage.setItem('permissionCodes', JSON.stringify(codes))
      } else {
        localStorage.removeItem('permissionCodes')
      }
    },
    // 设置权限树
    SET_PERMISSION_TREE(state, tree) {
      state.permissionTree = tree || []
      // 同步存储到localStorage
      if (tree && tree.length > 0) {
        localStorage.setItem('permissionTree', JSON.stringify(tree))
      } else {
        localStorage.removeItem('permissionTree')
      }
    },
    // 设置菜单权限列表
    SET_MENU_PERMISSIONS(state, permissions) {
      state.menuPermissions = permissions || []
    },
    // 设置按钮权限列表
    SET_BUTTON_PERMISSIONS(state, permissions) {
      state.buttonPermissions = permissions || []
    },
    // 设置API权限列表
    SET_API_PERMISSIONS(state, permissions) {
      state.apiPermissions = permissions || []
    },
    // 登录时一次性设置所有数据
    SET_LOGIN_DATA(state, loginData) {
      state.userInfo = loginData
      state.token = loginData.token
      
      // 设置权限相关数据
      state.permissionCodes = loginData.permissionCodes || []
      state.permissionTree = loginData.permissionTree || []
      state.menuPermissions = loginData.menuPermissions || []
      state.buttonPermissions = loginData.buttonPermissions || []
      state.apiPermissions = loginData.apiPermissions || []
      
      // 同步存储到localStorage
      if (loginData.token) {
        localStorage.setItem('token', loginData.token)
      }
      if (loginData.permissionCodes && loginData.permissionCodes.length > 0) {
        localStorage.setItem('permissionCodes', JSON.stringify(loginData.permissionCodes))
      }
      if (loginData.permissionTree && loginData.permissionTree.length > 0) {
        localStorage.setItem('permissionTree', JSON.stringify(loginData.permissionTree))
      }
    },
    // 清除所有权限数据
    CLEAR_PERMISSIONS(state) {
      state.permissionCodes = []
      state.permissionTree = []
      state.menuPermissions = []
      state.buttonPermissions = []
      state.apiPermissions = []
      localStorage.removeItem('permissionCodes')
      localStorage.removeItem('permissionTree')
    }
  },
  actions: {
    // 设置用户信息
    setUserInfo({ commit }, userInfo) {
      commit('SET_USER_INFO', userInfo)
    },
    // 设置令牌
    setToken({ commit }, token) {
      commit('SET_TOKEN', token)
    },
    // 用户登录
    login({ commit }, { token, userInfo }) {
      commit('SET_TOKEN', token)
      commit('SET_USER_INFO', userInfo)
    },
    // 用户退出登录
    logout({ commit }) {
      // 清除用户信息、令牌和权限数据
      commit('CLEAR_USER_INFO')
      commit('SET_TOKEN', null)
      commit('CLEAR_PERMISSIONS')
      // 可以在这里调用后端登出接口
      // return this.$api.user.logout()
    }
  },
  modules: {
    // 模块化存储
  }
}) 